/**
 * The kinds of document a report template makes (proposal.md 3.7).
 *
 * Phase 1 builds the one-entry kinds (shape A). The later kinds are named so a
 * layout that names one is refused with "not yet", never as a typo.
 *
 * Decision 9 of docs/plans/report-templates.md: the GST kinds are offered only
 * to a business with a `gstin` detail. Every other business gets the bill, the
 * receipt and the quotation.
 *
 * Off the package index, like `doc-layout.ts`: the phone never makes a document.
 */

/** The kinds a Phase 1 layout may name, in the order the seat offers them. */
export const DOC_KINDS = ['tax_invoice', 'bill_of_supply', 'sale_bill', 'payment_receipt', 'quotation'] as const;
export type DocKind = (typeof DOC_KINDS)[number];

/** Kinds of later phases. A layout naming one is refused as not yet built. */
export const LATER_DOC_KINDS = [
  'delivery_challan',
  'invoice_cum_bill_of_supply',
  'statement',
  'report_card',
  'fee_receipt',
  'rent_receipt',
  'payslip',
  'credit_note',
  'debit_note',
  'letter',
  'period_report',
  'dues_list',
] as const;
export type LaterDocKind = (typeof LATER_DOC_KINDS)[number];

export const DOC_SHAPES = ['entry', 'subject_period', 'period', 'list'] as const;
export type DocShape = (typeof DOC_SHAPES)[number];

/** A numbering series family. `sale_bill` has its own, so the invoice series stays consecutive. */
export type SeriesFamily = 'invoice' | 'bill' | 'receipt' | 'quotation';

export type DocKindInfo = {
  readonly shape: DocShape;
  /** A GST kind: needs a `gstin` detail, INR, and the Rule 46 or 49 checklist. */
  readonly gst: boolean;
  readonly family: SeriesFamily;
  /** The number must come from a gapless series (`numbering.mode = 'series'`). */
  readonly seriesRequired: boolean;
  /** The label key of the title `title.auto` prints. */
  readonly titleKey: string;
};

export const DOC_KIND_INFO: { readonly [K in DocKind]: DocKindInfo } = {
  tax_invoice: { shape: 'entry', gst: true, family: 'invoice', seriesRequired: true, titleKey: 'doc.title.tax_invoice' },
  bill_of_supply: { shape: 'entry', gst: true, family: 'invoice', seriesRequired: true, titleKey: 'doc.title.bill_of_supply' },
  sale_bill: { shape: 'entry', gst: false, family: 'bill', seriesRequired: false, titleKey: 'doc.title.bill' },
  payment_receipt: { shape: 'entry', gst: false, family: 'receipt', seriesRequired: false, titleKey: 'doc.title.payment_receipt' },
  quotation: { shape: 'entry', gst: false, family: 'quotation', seriesRequired: false, titleKey: 'doc.title.quotation' },
};

const KINDS: ReadonlySet<string> = new Set(DOC_KINDS);
const LATER: ReadonlySet<string> = new Set(LATER_DOC_KINDS);

export const isDocKind = (kind: string): kind is DocKind => KINDS.has(kind);
export const isLaterDocKind = (kind: string): kind is LaterDocKind => LATER.has(kind);
export const isGstKind = (kind: DocKind): boolean => DOC_KIND_INFO[kind].gst;

/**
 * The kinds a business may be offered (decision 9): the GST kinds only with a
 * `gstin` detail on file.
 */
export const docKindsOffered = (hasGstin: boolean): readonly DocKind[] =>
  DOC_KINDS.filter((kind) => hasGstin || !DOC_KIND_INFO[kind].gst);
